/**
 * Sdílené konstanty a výpočty modulu Směny.
 *
 * Vytaženo z původního `pages/Smeny.jsx` (5 059 řádků) beze změny chování —
 * každá část modulu je potřebuje a opisovat je do čtyř souborů by znamenalo,
 * že se dřív nebo později rozejdou.
 */
import { PlayCircle, CheckCircle, Activity } from 'lucide-react';

// Diakritiku odstraní pro tolerantní porovnání (filtr "polen" najde "Poleno", "Polák").
export const stripDiacritics = (s) => String(s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

// Pevné 3 fáze směny (zahajeni → prubeh → ukonceni). Název, barva, ikona jsou neměnné.
// V editoru se nastavuje jen Zahájení a Ukončení; Průběh = zbytek a dopočítá se sám.
export const FIXED_FAZE_META = {
    zahajeni: { nazev: 'Zahájení', barva: '#0CC0DF', Icon: PlayCircle, defaultDelkaMin: 15 },
    prubeh:   { nazev: 'Průběh',   barva: '#975ACD', Icon: Activity,    defaultDelkaMin: null },
    ukonceni: { nazev: 'Ukončení', barva: '#FF8A00', Icon: CheckCircle, defaultDelkaMin: 15 },
};

export const FAZE_ORDER = ['zahajeni', 'prubeh', 'ukonceni'];

// Fáze editovatelné v modálu směny/šablony — Průběh se nezobrazuje (je to zbytek).
export const EDITOVATELNE_FAZE = ['zahajeni', 'ukonceni'];

// Vrátí 3 pevné fáze, případně zachová aktivity/stav z existujících.
export function initFaze(existing) {
    const byId = {};
    const legacy = [];
    if (Array.isArray(existing)) {
        existing.forEach(f => {
            if (FAZE_ORDER.includes(f?.id)) byId[f.id] = f;
            else if (f?.aktivity) legacy.push(...f.aktivity);
        });
    }
    // Pauzy se řeší samostatným oddílem Přestávky — vyfiltrujeme je z aktivit fází.
    const bezPauz = (arr) => (arr || []).filter(a => a?.typ !== 'pauza');
    return FAZE_ORDER.map(id => {
        const e = byId[id] || {};
        const meta = FIXED_FAZE_META[id];
        const aktivity = id === 'prubeh' && legacy.length > 0
            ? bezPauz([...(e.aktivity || []), ...legacy])
            : bezPauz(e.aktivity);
        return {
            id,
            delka_min: e.delka_min ?? meta.defaultDelkaMin,
            aktivity,
            stav: e.stav || 'todo',
            dokonceno_at: e.dokonceno_at || null,
        };
    });
}

// Vypočítá absolutní cas_od/cas_do 3 fází na základě cas_od/cas_do směny.
export function computeFazeTimes(smenaOd, smenaDo, faze) {
    if (!smenaOd || !smenaDo) return {};
    const toMin = h => { const [a, b] = h.split(':').map(Number); return a * 60 + b; };
    const toStr = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const odMin = toMin(smenaOd);
    const doMin = toMin(smenaDo);
    const zahajeniDelka = Math.max(0, Number(faze?.find(f => f.id === 'zahajeni')?.delka_min ?? 15));
    const ukonceniDelka = Math.max(0, Number(faze?.find(f => f.id === 'ukonceni')?.delka_min ?? 15));
    const zahajeniDoMin = odMin + zahajeniDelka;
    const ukonceniOdMin = Math.max(zahajeniDoMin, doMin - ukonceniDelka);
    return {
        zahajeni: { cas_od: toStr(odMin), cas_do: toStr(zahajeniDoMin) },
        prubeh:   { cas_od: toStr(zahajeniDoMin), cas_do: toStr(ukonceniOdMin) },
        ukonceni: { cas_od: toStr(ukonceniOdMin), cas_do: toStr(doMin) },
    };
}

export const SECTION_COLOR = '#B47AEA';        // sekce Tým — tmavý motiv
export const SECTION_COLOR_SVETLY = '#831BE4'; // týž tón pro světlý (kontrast na bílé)

/** Akcent modulu podle motivu. Použij všude, kde je po ruce `isDark`. */
export const barvaSmen = (isDark) => (isDark ? SECTION_COLOR : SECTION_COLOR_SVETLY);

export const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

// Svátky se fetchují z API — viz svatkyData state v komponentě

// Výpočet odpracovaných hodin: (do − od) − neplacené přestávky.
export function vypocetHodin(casOd, casDo, pauzy = []) {
    if (!casOd || !casDo) return '';
    const [oh, om] = casOd.split(':').map(Number);
    const [dh, dm] = casDo.split(':').map(Number);
    let celkem = (dh + dm / 60) - (oh + om / 60);
    if (celkem <= 0) return '';
    (pauzy || []).forEach(p => {
        if (!p.od || !p.do || p.placena) return;
        const [poh, pom] = p.od.split(':').map(Number);
        const [pdh, pdm] = p.do.split(':').map(Number);
        celkem -= (pdh + pdm / 60) - (poh + pom / 60);
    });
    return Math.round(celkem * 2) / 2;
}

// Formát hodin: 0→"–", 11.0→"11h", 11.5→"11.5h"
export function fmtH(h) {
    if (!h || h === 0) return '–';
    const n = Number(h);
    if (isNaN(n) || n === 0) return '–';
    return (n % 1 === 0 ? n : n.toFixed(1)) + 'h';
}
export function fmtKc(n) { if (!n || n === 0) return ''; return Math.round(n).toLocaleString('cs') + ' Kč'; }

// Lokální datum bez timezone posunu
export const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
export const todayStr = () => toLocalDateStr(new Date());

export const dnyLabels = { 1: 'Po', 2: 'Út', 3: 'St', 4: 'Čt', 5: 'Pá', 6: 'So', 7: 'Ne' };

export const stavMeta = {
    planovana:   { label: 'Plánovaná',   color: '#3B82F6' },
    potvrzena:   { label: 'Potvrzená',   color: '#10B981' },
    probihajici: { label: 'Probíhající', color: '#F59E0B' },
    dokoncena:   { label: 'Dokončená',   color: '#6B7280' },
    zrusena:     { label: 'Zrušená',     color: '#EF4444' },
};

// ─────────────────────────────────────────────────────────────
// ── Nepřítomnost sub-tab ──
export const TYP_CONFIG = {
    dovolena: { label: 'Dovolená', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
    nemoc:    { label: 'Nemoc',    color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
    osobni:   { label: 'Osobní',   color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    jine:     { label: 'Jiné',     color: '#6B7280', bg: 'rgba(107,114,128,0.12)' },
};
export const STAV_CONFIG = {
    ceka:      { label: 'Čeká', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    schvaleno: { label: 'Schváleno', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
    zamitnuto: { label: 'Zamítnuto', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
};
